/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Button, Form, Input, InputNumber, message, Modal, Select, Space } from 'antd';
import { useDispatch } from 'react-redux';
import {
  addManualBeaconService,
  getBeaconSettingsService,
} from '../../../services/appCmsServices/BeaconService';
import { MAX_MAJOR_MINOR } from './Constants';

const { Option } = Select;

const UUID_REGEX = /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/;
const MAC_REGEX = /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/;
const MAX_UNIQUE_ID_LENGTH = 250;

const formatMac = raw => {
  const hex = (raw || '')
    .replace(/[^0-9a-fA-F]/g, '')
    .toUpperCase()
    .slice(0, 12);
  return hex.match(/.{1,2}/g) ? hex.match(/.{1,2}/g).join(':') : '';
};

const AddBeaconModal = ({ visible, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [siteUuid, setSiteUuid] = useState('');
  const [beaconTypes, setBeaconTypes] = useState([]);
  const [beaconType, setBeaconType] = useState();

  useEffect(() => {
    if (!visible) {
      return;
    }
    dispatch(getBeaconSettingsService())
      .then(response => {
        const settings = response.data || {};
        const uuid = settings.beaconUuid || '';
        const types = settings.beaconTypes || [];
        setSiteUuid(uuid);
        setBeaconTypes(types);
        setBeaconType(types[0] && types[0].name);
        form.setFieldsValue({ uuid });
      })
      .catch(() => message.error("Could not load this site's beacon UUID"));
  }, [visible]);

  const selectedType = beaconTypes.find(type => type.name === beaconType);
  const usesSiteUuid = !!(selectedType && selectedType.usesSiteUuid);

  const resetType = () => setBeaconType(beaconTypes[0] && beaconTypes[0].name);

  const handleClose = () => {
    form.resetFields();
    resetType();
    onClose();
  };

  const handleTypeChange = type => {
    setBeaconType(type);
    form.setFieldsValue({ uuid: siteUuid });
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }
    const uuid = usesSiteUuid ? siteUuid : values.uuid.trim();
    if (!beaconType || !uuid) {
      message.error("This site's beacon settings are not available, please try again");
      return;
    }

    setSaving(true);
    try {
      await dispatch(
        addManualBeaconService({
          uniqueId: values.uniqueId.trim(),
          beaconType,
          uuid,
          major: values.major,
          minor: values.minor,
          mac: values.mac,
        }),
      );
      message.success('Beacon added!');
      form.resetFields();
      resetType();
      onSuccess();
    } catch (error) {
      const data = error.response && error.response.data;
      message.error((data && data.debugMessage) || 'Could not add the beacon');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Add Beacon"
      open={visible}
      onCancel={handleClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSubmit}>
          Add Beacon
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Beacon Type">
          <Select
            value={beaconType}
            onChange={handleTypeChange}
            disabled={beaconTypes.length <= 1}
          >
            {beaconTypes.map(type => (
              <Option key={type.name} value={type.name}>
                {type.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Unique ID"
          name="uniqueId"
          rules={[
            { required: true, whitespace: true, message: 'Please enter the Unique ID' },
            {
              max: MAX_UNIQUE_ID_LENGTH,
              message: 'Unique ID can be at most ' + MAX_UNIQUE_ID_LENGTH + ' characters',
            },
          ]}
        >
          <Input placeholder="Unique ID of the beacon" maxLength={MAX_UNIQUE_ID_LENGTH} />
        </Form.Item>

        <Form.Item
          label="UUID"
          name="uuid"
          extra={usesSiteUuid ? beaconType + " beacons use this site's UUID" : undefined}
          rules={
            usesSiteUuid
              ? []
              : [
                  { required: true, whitespace: true, message: 'Please enter the UUID' },
                  {
                    pattern: UUID_REGEX,
                    message: 'UUID must look like xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
                  },
                ]
          }
        >
          <Input disabled={usesSiteUuid} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
        </Form.Item>

        <Space align="start">
          <Form.Item
            label="Major"
            name="major"
            rules={[{ required: true, message: 'Please enter the Major' }]}
          >
            <InputNumber min={0} max={MAX_MAJOR_MINOR} precision={0} style={{ width: 180 }} />
          </Form.Item>
          <Form.Item
            label="Minor"
            name="minor"
            rules={[{ required: true, message: 'Please enter the Minor' }]}
          >
            <InputNumber min={0} max={MAX_MAJOR_MINOR} precision={0} style={{ width: 180 }} />
          </Form.Item>
        </Space>

        <Form.Item
          label="MAC Address"
          name="mac"
          getValueFromEvent={e => formatMac(e.target.value)}
          rules={[
            { required: true, message: 'Please enter the MAC address' },
            {
              pattern: MAC_REGEX,
              message: 'MAC address must look like AA:BB:CC:DD:EE:FF',
            },
          ]}
        >
          <Input placeholder="AA:BB:CC:DD:EE:FF" maxLength={17} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddBeaconModal;
